import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, AlertCircle, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { KidStatBadges } from "@/components/kid/KidStatBadges";
import { apiRequest } from "@/lib/queryClient";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SkillAverages {
  battingGrip: number | null;
  battingStance: number | null;
  battingFootwork: number | null;
  battingTransfer: number | null;
  battingHead: number | null;
  bowlingLoad: number | null;
  bowlingBfc: number | null;
  bowlingBrace: number | null;
  bowlingRelease: number | null;
  fieldingCatch: number | null;
  fieldingThrow: number | null;
  fieldingGround: number | null;
}

interface BatchKid {
  id: number;
  name: string;
  compositeScore: number | null;
  totalSessions: number;
  attendedSessions: number;
  avgBattingScore: number | null;
  avgBowlingScore: number | null;
  avgFieldingScore: number | null;
}

interface BatchAnalytics {
  batchId: number;
  batchName: string;
  ageGroup: string;
  kidCount: number;
  avgCompositeScore: number | null;
  skillAverages: SkillAverages;
  kids: BatchKid[];
}

// ─── Skill definitions ────────────────────────────────────────────────────────

interface SkillDef {
  key: keyof SkillAverages;
  label: string;
  hint: string;  // plain-English explanation shown in parent view
}

const SKILL_GROUPS: { discipline: string; skills: SkillDef[] }[] = [
  {
    discipline: "Batting",
    skills: [
      { key: "battingGrip",     label: "Grip",            hint: "How the bat handle is held" },
      { key: "battingStance",   label: "Stance",          hint: "Starting position at the crease" },
      { key: "battingFootwork", label: "Footwork",        hint: "Movement into position to play the ball" },
      { key: "battingTransfer", label: "Weight transfer", hint: "Shifting body weight into the shot" },
      { key: "battingHead",     label: "Head position",   hint: "Eyes level and still through the shot" },
    ],
  },
  {
    discipline: "Bowling",
    skills: [
      { key: "bowlingLoad",    label: "Run-up load",     hint: "Hip coil and arm position in the gather" },
      { key: "bowlingBfc",     label: "Ball contact",    hint: "Wrist position and seam alignment at release" },
      { key: "bowlingBrace",   label: "Front-arm brace", hint: "Non-bowling arm pull-down for pace and control" },
      { key: "bowlingRelease", label: "Release",         hint: "Angle and timing of delivery" },
    ],
  },
  {
    discipline: "Fielding",
    skills: [
      { key: "fieldingCatch",  label: "Catching",       hint: "Secure hands and soft hands under pressure" },
      { key: "fieldingThrow",  label: "Throwing",       hint: "Accuracy and arm strength" },
      { key: "fieldingGround", label: "Ground fielding", hint: "Low body position and safe hands on the floor" },
    ],
  },
];

// Flat list for skill gap table
const SKILL_LABELS = SKILL_GROUPS.flatMap((g) =>
  g.skills.map(({ key, label }) => ({ key, label }))
);

// ─── Colour helpers ───────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 4) return "#639922";   // green
  if (score >= 3) return "#BA7517";   // amber
  return "#A32D2D";                   // red
}

function scoreTextClass(score: number): string {
  if (score >= 4) return "text-green-700";
  if (score >= 3) return "text-amber-700";
  return "text-red-700";
}

// ─── Skill bars ───────────────────────────────────────────────────────────────

function SkillBarsSection({
  skillAverages,
  showHints,
}: {
  skillAverages: SkillAverages;
  showHints: boolean;
}) {
  // Compute per-discipline averages
  const discAverages = SKILL_GROUPS.map(({ discipline, skills }) => {
    const vals = skills.map((s) => skillAverages[s.key]).filter((v): v is number => v !== null);
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    return { discipline, avg };
  });

  // Focus areas: bottom 2 skills overall
  const allSkills = SKILL_GROUPS.flatMap((g) => g.skills);
  const ranked = allSkills
    .map((s) => ({ label: s.label, score: skillAverages[s.key] }))
    .filter((s): s is { label: string; score: number } => s.score !== null)
    .sort((a, b) => a.score - b.score)
    .slice(0, 2);

  return (
    <div className="space-y-4">
      {/* Discipline summary chips */}
      <div className="grid grid-cols-3 gap-3">
        {discAverages.map(({ discipline, avg }) => (
          <div
            key={discipline}
            className="bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm text-center"
          >
            <p className="text-xs text-gray-500 mb-1">{discipline}</p>
            {avg !== null ? (
              <>
                <p
                  className="text-2xl font-bold"
                  style={{ color: scoreColor(avg) }}
                >
                  {avg.toFixed(1)}
                </p>
                <p className="text-xs text-gray-400">/ 5</p>
              </>
            ) : (
              <p className="text-xl font-bold text-gray-300">—</p>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-gray-500 flex-wrap px-1">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full bg-green-700" />
          Strong (4–5)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-700" />
          On track (3–3.9)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full bg-red-700" />
          Needs work (&lt;3)
        </span>
      </div>

      {/* Focus areas callout */}
      {ranked.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 mb-1.5">
            <Target className="h-3.5 w-3.5" aria-hidden="true" />
            Focus areas this session
          </p>
          {ranked.map(({ label, score }) => (
            <p key={label} className="text-xs text-amber-700 leading-relaxed">
              · <strong>{label}</strong> — avg {score.toFixed(1)} / 5
            </p>
          ))}
        </div>
      )}

      {/* Per-discipline skill bars */}
      {SKILL_GROUPS.map(({ discipline, skills }) => {
        const vals = skills.map((s) => skillAverages[s.key]).filter((v): v is number => v !== null);
        const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;

        return (
          <div key={discipline} className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-700">{discipline}</span>
              {avg !== null && (
                <span className="text-xs text-gray-500">avg {avg.toFixed(1)} / 5</span>
              )}
            </div>
            <div className="divide-y divide-gray-50">
              {skills.map((skill) => {
                const score = skillAverages[skill.key];
                if (score === null) return null;
                return (
                  <div key={skill.key} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="w-36 flex-shrink-0">
                      <p className="text-sm text-gray-800">{skill.label}</p>
                      {showHints && (
                        <p className="text-xs text-gray-400 leading-tight mt-0.5">{skill.hint}</p>
                      )}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${(score / 5) * 100}%`,
                          backgroundColor: scoreColor(score),
                        }}
                      />
                    </div>
                    <span
                      className={`text-xs font-semibold w-7 text-right tabular-nums ${scoreTextClass(score)}`}
                    >
                      {score.toFixed(1)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Skill gap table ──────────────────────────────────────────────────────────

function SkillGapTable({ skillAverages }: { skillAverages: SkillAverages }) {
  const entries = SKILL_LABELS
    .map(({ key, label }) => ({ label, value: skillAverages[key] }))
    .filter((e): e is { label: string; value: number } => e.value !== null)
    .sort((a, b) => a.value - b.value);

  if (entries.length === 0) {
    return <p className="text-sm text-gray-400 italic">No skill data captured yet.</p>;
  }

  const focusKeys = new Set(entries.slice(0, 2).map((e) => e.label));

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white" data-testid="skill-gap-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <th className="text-left px-4 py-3 font-semibold text-gray-700">Skill</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-700">Avg (/ 5)</th>
            <th className="text-left px-4 py-3 font-semibold text-gray-700"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {entries.map(({ label, value }) => (
            <tr key={label} className={focusKeys.has(label) ? "bg-amber-50" : ""}>
              <td className="px-4 py-2.5 font-medium text-gray-800">{label}</td>
              <td className="px-4 py-2.5 text-gray-700 font-mono">{value.toFixed(1)}</td>
              <td className="px-4 py-2.5">
                {focusKeys.has(label) && (
                  <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-200 border">
                    Focus area
                  </Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BatchAnalyticsPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const [showHints, setShowHints] = useState(true);

  const { data: analytics, isLoading, isError, refetch } = useQuery<BatchAnalytics>({
    queryKey: ["batch-analytics", batchId],
    queryFn: async () => {
      const res: any = await apiRequest("GET", `/api/batches/${batchId}/analytics`);
      return res.data as BatchAnalytics;
    },
    enabled: !!batchId,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="px-4 py-6 md:px-6 md:py-8 max-w-4xl mx-auto space-y-5" data-testid="batch-analytics-page">
        <Skeleton className="h-8 w-16" />
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-72 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  if (isError || !analytics) {
    return (
      <div className="px-4 py-12 text-center text-gray-500 space-y-3" data-testid="batch-analytics-error">
        <AlertCircle className="h-8 w-8 text-red-400 mx-auto" />
        <p className="font-medium">Could not load batch analytics.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/coach/batches")}>← Back to Batches</Button>
        </div>
      </div>
    );
  }

  const hasSkillData = Object.values(analytics.skillAverages).some((v) => v !== null);
  const hasKids = analytics.kids.length > 0;

  return (
    <div className="px-4 py-6 md:px-6 md:py-8 max-w-4xl mx-auto space-y-6" data-testid="batch-analytics-page">
      {/* Back */}
      <Button variant="ghost" size="sm" className="px-0 text-gray-500 hover:text-gray-700" onClick={() => navigate("/coach/batches")}>
        <ArrowLeft className="h-4 w-4 mr-1" />
        Batches
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="batch-analytics-name">
            {analytics.batchName}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="secondary" className="text-xs">{analytics.ageGroup}</Badge>
            <span className="text-sm text-gray-500">{analytics.kidCount} players</span>
          </div>
        </div>
      </div>

      {/* Composite score tile */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-100 rounded-xl px-5 py-4 shadow-sm text-center" data-testid="composite-score-tile">
          <p className="text-xs text-gray-500 mb-1">Avg Composite Score</p>
          <p className="text-3xl font-bold text-gray-900">
            {analytics.avgCompositeScore !== null ? analytics.avgCompositeScore.toFixed(1) : "—"}
            <span className="text-sm font-normal text-gray-400 ml-1">/ 5</span>
          </p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl px-5 py-4 shadow-sm text-center">
          <p className="text-xs text-gray-500 mb-1">Players</p>
          <p className="text-3xl font-bold text-gray-900">{analytics.kidCount}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl px-5 py-4 shadow-sm text-center">
          <p className="text-xs text-gray-500 mb-1">With Session Data</p>
          <p className="text-3xl font-bold text-gray-900">
            {analytics.kids.filter((k) => k.totalSessions > 0).length}
          </p>
        </div>
      </div>

      {/* Skill profile */}
      {hasSkillData ? (
        <div data-testid="batch-radar-chart">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Batch Skill Profile
            </h2>
            {/* Parent / Coach toggle */}
            <div className="flex rounded-full bg-gray-100 p-0.5 text-xs">
              <button
                onClick={() => setShowHints(true)}
                className={`px-3 py-1 rounded-full transition-colors ${showHints ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500"}`}
              >
                Parent view
              </button>
              <button
                onClick={() => setShowHints(false)}
                className={`px-3 py-1 rounded-full transition-colors ${!showHints ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500"}`}
              >
                Coach detail
              </button>
            </div>
          </div>
          <SkillBarsSection skillAverages={analytics.skillAverages} showHints={showHints} />
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl p-8 shadow-sm text-center text-gray-400" data-testid="batch-radar-empty">
          <p className="font-medium text-gray-500">No skill data yet</p>
          <p className="text-sm mt-1">Scores will appear after sessions are logged for this batch.</p>
        </div>
      )}

      {/* Ranked kid list */}
      {hasKids ? (
        <div data-testid="batch-kids-list">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            Players — Ranked by Composite Score
          </h2>
          <div className="space-y-4">
            {analytics.kids.map((kid, idx) => (
              <div key={kid.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-400 w-5">#{idx + 1}</span>
                    <Link
                      to={`/coach/players/${kid.id}`}
                      className="font-semibold text-gray-900 hover:text-blue-600 transition-colors text-sm"
                      data-testid={`kid-analytics-link-${kid.id}`}
                    >
                      {kid.name}
                    </Link>
                  </div>
                  {kid.compositeScore !== null && (
                    <span className="text-sm font-bold text-gray-700">
                      {kid.compositeScore.toFixed(1)}<span className="text-xs font-normal text-gray-400 ml-0.5">/ 5</span>
                    </span>
                  )}
                </div>
                <KidStatBadges kid={kid} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic text-center py-8" data-testid="batch-kids-empty">
          No active players in this batch yet.
        </p>
      )}

      {/* Skill gap table */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
          Skill Gap Analysis — Batch Focus Areas
        </h2>
        <SkillGapTable skillAverages={analytics.skillAverages} />
      </div>
    </div>
  );
}
